'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useUi } from '@weareconceptstudio/core';
import { useAddressContext } from '@weareconceptstudio/account';
import DeliveryTemplate from '@/modules/Checkout/DeliveryTemplate';
import {
	consumeFulfillmentRestorePending,
	readFulfillmentRestorePending,
	readNavigationIntent,
} from '@/helpers/fulfillmentNavigation';

export const useFulfillmentRedirectRestore = () => {
	const { openPopup } = useUi();
	const { selectedAddresses, hasAddressType } = useAddressContext();
	const restoringRef = useRef(false);

	const openRestoredDeliveryTemplate = useCallback(() => {
		const intent = readNavigationIntent();
		const restore = consumeFulfillmentRestorePending();

		if (!intent || !restore || restoringRef.current) return;

		restoringRef.current = true;
		requestAnimationFrame(() => {
			openPopup(
				<DeliveryTemplate
					initialTab={restore.initialTab || 'delivery'}
					redirectAfterConfirm={intent}
					autoConfirmWhenReady
				/>,
				{ className: 'delivery-template-popup' }
			);
			restoringRef.current = false;
		});
	}, [openPopup]);

	useEffect(() => {
		const onPendingSaved = () => openRestoredDeliveryTemplate();
		window.addEventListener('pendingAddressSaved', onPendingSaved);
		return () => window.removeEventListener('pendingAddressSaved', onPendingSaved);
	}, [openRestoredDeliveryTemplate]);

	useEffect(() => {
		if (!readFulfillmentRestorePending() || !readNavigationIntent()) return;

		const addressId = hasAddressType ? selectedAddresses?.shipping?.id : selectedAddresses?.id;
		if (!addressId) return;

		openRestoredDeliveryTemplate();
	}, [hasAddressType, openRestoredDeliveryTemplate, selectedAddresses]);
};
